import { Card, Divider, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import ContractImage from "@/assets/talk2pay/contract.svg";
import IdentityImage from "@/assets/talk2pay/identity.svg";
import MDAvatar from "@/components/atoms/MDAvatar";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import useGetDocumentation from "@/hooks/requests/t2Pay/use-get-documentation";
import usePagination from "@/hooks/use-pagination";
import {
  Talk2PayDocument,
  Talk2PayDocumentTypes,
  Talk2PayStatus,
} from "@/interfaces/talk2pay.iterfaces";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";
import borders from "@/themes/base/borders";

type Props = {
  messages: T2PayHomeMessages;
};

export default function DocumentsTab({ messages }: Props) {
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const { data, isLoading, error } = useGetDocumentation({ pageNumber, pageSize });
  const { borderWidth, borderColor } = borders;

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h5">{messages.documentsTabTitle}</MDTypography>
        <MDTypography variant="body2" color="text" fontWeight={"regular"}>
          {messages.documentsTabDescription}
        </MDTypography>
      </MDBox>

      <MDBox px={3} pb={1.5}>
        <MDTypography variant="h6" color="dark">
          {messages.directorDocumentsTitle}
        </MDTypography>
      </MDBox>

      <MDBox p={3} pt={0}>
        {isLoading
          ? Array.from(new Array(3)).map((_, index) => (
              <Grid size={12} key={index}>
                <Skeleton variant="rectangular" width="100%" height={60} />
              </Grid>
            ))
          : data?.items.map((document: Talk2PayDocument, index) => {
              const isIdentification = document.type === Talk2PayDocumentTypes.IDENTIFICATION;

              return (
                <React.Fragment key={index}>
                  <MDBox
                    display="flex"
                    border={`${borderWidth[1]} solid ${borderColor}`}
                    borderRadius="md"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    p={3}
                  >
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      gap={2}
                    >
                      <MDAvatar
                        src={isIdentification ? IdentityImage : ContractImage}
                        alt="teste"
                        size="xl"
                        sx={{
                          borderRadius: ({ borders: { borderRadius } }) => borderRadius.xl,
                        }}
                      />

                      <MDBox>
                        <MDTypography variant="body2" fontWeight="medium" color="dark">
                          {document.title} &nbsp;
                        </MDTypography>

                        {document.status === Talk2PayStatus.REJECTED && (
                          <MDTypography
                            variant="caption"
                            sx={{ cursor: "pointer", fontSize: "14px" }}
                            color="text"
                          >
                            {messages.documentRejectedMessage} {messages.uploadInstruction}
                          </MDTypography>
                        )}

                        {document.status === Talk2PayStatus.NOT_SENT && (
                          <MDTypography
                            variant="caption"
                            sx={{ cursor: "pointer", fontSize: "14px" }}
                            color="text"
                          >
                            {messages.documentNotSentMessage} {messages.uploadInstructionNotSent}
                          </MDTypography>
                        )}

                        {document.status === Talk2PayStatus.PENDING && (
                          <MDTypography
                            variant="caption"
                            sx={{ cursor: "pointer", fontSize: "14px" }}
                            color="warning"
                          >
                            {messages.documentPendingMessage} {messages.uploadInstructionPending}
                          </MDTypography>
                        )}

                        {document.status === Talk2PayStatus.AWAITING_APPROVAL && (
                          <MDTypography
                            variant="caption"
                            sx={{ cursor: "pointer", fontSize: "14px" }}
                            color="info"
                          >
                            {messages.documentAwaitingApprovalMessage}{" "}
                            {messages.uploadInstructionAwaitingApproval}
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>

                    <MDBox
                      display="flex"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      flexDirection={{ xs: "column", sm: "row" }}
                    >
                      <MDBox mx={{ xs: 0, sm: 2 }} mb={{ xs: 1, sm: 0 }}>
                        {document.status === Talk2PayStatus.APPROVED ? (
                          <MDTypography variant="body2" color="success" fontWeight="bold">
                            {messages.approvedMessage}
                          </MDTypography>
                        ) : (
                          <MDButton variant="gradient" color="dark" href={document.onboardingUrl}>
                            {messages.uploadButtonLabel}
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  <Divider />
                </React.Fragment>
              );
            })}
      </MDBox>

      {error && (
        <AlertBlock
          title={messages.errorTitle}
          description={messages.errorDescription}
          alt={messages.errorAlt}
        />
      )}
    </Card>
  );
}
